import {INTRO_STANDARD} from '../content/intro-course';
export const ONBOARDING_LEVELS = ['beginner', 'hsk1', 'hsk2', 'hsk3', 'hsk4', 'hsk5', 'hsk6'] as const;

export type OnboardingLevelSelection = '' | (typeof ONBOARDING_LEVELS)[number];
export type SelectedOnboardingLevel = Exclude<OnboardingLevelSelection, ''>;

// The v1 handoff explicitly has no preselected level.
export const DEFAULT_ONBOARDING_LEVEL: OnboardingLevelSelection = '';
export const DEFAULT_PRIVACY_CONSENT = false;

/** A single value represents the complete level selection, so two options cannot be active. */
export function selectOnboardingLevel(level: SelectedOnboardingLevel): SelectedOnboardingLevel {
  return level;
}

export function levelForHsk(hskLevel: number): SelectedOnboardingLevel {
  if (!Number.isInteger(hskLevel) || hskLevel < 1 || hskLevel > 6) {
    throw new RangeError(`Unsupported HSK level: ${hskLevel}`);
  }
  return `hsk${hskLevel}` as SelectedOnboardingLevel;
}

export function courseIdForSelection(
  selection: OnboardingLevelSelection,
  courses: Array<{ id: string; hskLevel?: { level?: number; standardCode?: string } }> = [],
): string | undefined {
  if (!selection) return undefined;
  if(selection==='beginner')return courses.find(c=>c.hskLevel?.standardCode===INTRO_STANDARD)?.id;
  const hskLevel = Number(selection.slice(3));
  return courses.find((course) => course.hskLevel?.level === hskLevel && course.hskLevel?.standardCode!==INTRO_STANDARD)?.id;
}

export function skipOnboardingIntro(currentSlide: number): number {
  return currentSlide < 2 ? 2 : currentSlide;
}

export function getOnboardingValidation(
  showErrors: boolean,
  selection: OnboardingLevelSelection,
  consentGiven: boolean,
  courseAvailable = true,
): { level: boolean; consent: boolean } {
  return {
    level: showErrors && (!selection || !courseAvailable),
    consent: showErrors && !consentGiven,
  };
}

export function canSubmitOnboardingSetup(
  selection: OnboardingLevelSelection,
  consentGiven: boolean,
  courseAvailable: boolean,
  pending = false,
): boolean {
  return !pending && Boolean(selection && consentGiven && courseAvailable);
}

export function canProceedFromOnboardingSlide(
  slide: number,
  selection: OnboardingLevelSelection,
  consentGiven: boolean,
  courseAvailable: boolean,
  pending = false,
): boolean {
  if (slide === 2) return canSubmitOnboardingSetup(selection, consentGiven, courseAvailable, pending);
  return !pending && (slide === 0 || slide === 1);
}

export function canNavigateToOnboardingSlide(
  currentSlide: number,
  targetSlide: number,
  selection: OnboardingLevelSelection,
  consentGiven: boolean,
  courseAvailable: boolean,
  pending = false,
): boolean {
  if (targetSlide <= currentSlide) return false;
  if (targetSlide !== currentSlide + 1) return false;
  if (currentSlide === 2) return false;
  return canProceedFromOnboardingSlide(currentSlide, selection, consentGiven, courseAvailable, pending);
}
